import { useState, useEffect } from 'react';
import { useApp } from '../store';
import { api } from '../api';
import { playSound } from '../sounds';
import { Icon, Button } from './ui';

// =============================================================================
// VoiceRejoinPrompt — after a refresh or unexpected drop, offer to rejoin the
// channel we were in.
//
// This is deliberately a *light*, livekit-free component living in its own
// module so it can be imported eagerly and rendered unconditionally by App
// (it must be able to appear on a cold reload, before — and without — the heavy
// lazy voice chunk being loaded). Rejoining flips `activeVoice`, which is what
// then mounts the lazy VoiceConnection driver. Keep this out of
// VoiceConnection.tsx (the deferred chunk).
// =============================================================================
export function VoiceRejoinPrompt() {
  const lastVoiceChannelId = useApp((s) => s.lastVoiceChannelId);
  const lastVoiceChannelAt = useApp((s) => s.lastVoiceChannelAt);
  const setLastVoice = useApp((s) => s.setLastVoice);
  const setActiveVoice = useApp((s) => s.setActiveVoice);
  const activeVoice = useApp((s) => s.activeVoice);
  const user = useApp((s) => s.user);
  const [channelInfo, setChannelInfo] = useState<{
    serverId: string;
    channelName: string;
  } | null>(null);
  const [dismissed, setDismissed] = useState(false);

  // Only consider rejoin if we have a recent (< 2.5min) last-voice marker AND
  // we're not already connected. Older than that and it's stale.
  const shouldOffer =
    !!user &&
    !activeVoice &&
    !dismissed &&
    !!lastVoiceChannelId &&
    !!lastVoiceChannelAt &&
    Date.now() - lastVoiceChannelAt < 150_000;

  // Look up channel + server info for the prompt
  useEffect(() => {
    if (!shouldOffer || !lastVoiceChannelId) return;
    let cancelled = false;
    (async () => {
      try {
        const servers = await api.listServers();
        for (const s of servers) {
          const channels = await api.listChannels(s.id);
          const ch = channels.find((c) => c.id === lastVoiceChannelId);
          if (ch && !cancelled) {
            setChannelInfo({
              serverId: s.id,
              channelName: ch.name,
            });
            return;
          }
        }
        if (!cancelled) setLastVoice(null);
      } catch {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [shouldOffer, lastVoiceChannelId, setLastVoice]);

  function rejoin() {
    if (!lastVoiceChannelId || !channelInfo) return;
    if (useApp.getState().soundsEnabled) playSound('join');
    setActiveVoice({
      serverId: channelInfo.serverId,
      channelId: lastVoiceChannelId,
      channelName: channelInfo.channelName,
    });
    setDismissed(true);
  }

  function dismiss() {
    setDismissed(true);
    setLastVoice(null);
  }

  if (!shouldOffer || !channelInfo) return null;

  return (
    <div className="fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] left-1/2 -translate-x-1/2 z-50 aim-window animate-slide-up shadow-aim-window">
      <div className="aim-window-title py-1!">Rejoin voice?</div>
      <div className="bg-surface p-3 flex items-center gap-3">
        <Icon name="phone" size={16} className="text-online" />
        <div className="text-sm text-text">
          You were in <strong>#{channelInfo.channelName}</strong>
        </div>
        <Button onClick={rejoin} variant="primary">
          Rejoin
        </Button>
        <button
          onClick={dismiss}
          className="text-text-subtle hover:text-text px-1 leading-none text-base"
          title="Dismiss" aria-label="Dismiss"
        >
          ×
        </button>
      </div>
    </div>
  );
}
